'use client';

import { produce } from 'immer';
import { createStore } from 'zustand';

import {
	APPLICANT_MODE_WORKSPACE_ID,
	Workspace,
	WorkspacePreferences,
} from '@/features/user-settings/types/workspace-types';

import { ISUserDetails } from '@/shared/services/user/user.types';

import { getDashboardMenu } from '@/utils/common';

export interface WorkspaceSelector {
	showSelector: boolean;
	showCreateWorkspace: boolean;
	callback?: () => void;
	source?: string;
}

export interface PageAccessDenied {
	workspace: boolean;
	feature: boolean;
	message: string;
}

export interface AppWrapperStoreState {
	// User data
	userDetails: ISUserDetails | null;
	workspaces: Omit<Workspace, 'pageUsers' | 'isPro'>[];
	selectedWorkspaceId: string;
	isApplicantMode: boolean;
	selectedWorkspace: Omit<Workspace, 'pageUsers' | 'isPro'> | null;
	dashboardMenu: { name: string; path: string }[];

	// UI state
	workspaceSelector: WorkspaceSelector;
	pageAccessDenied: PageAccessDenied;

	// Loading states
	isAppLoading: boolean;
	isRefetching: boolean;
	isAnonymousMode: boolean;

	// Actions
	setUserDetails: (userDetails: ISUserDetails | null) => void;
	/** Shallow-merge a partial update into userDetails without triggering workspace re-derivation. */
	patchUserDetails: (patch: Partial<ISUserDetails>) => void;
	setWorkspaces: (workspaces: Omit<Workspace, 'pageUsers' | 'isPro'>[]) => void;
	setSelectedWorkspaceId: (workspaceId: string) => void;

	setWorkspaceSelector: (selector: Partial<WorkspaceSelector>) => void;
	resetWorkspaceSelector: () => void;
	setPageAccessDenied: (access: Partial<PageAccessDenied>) => void;
	resetPageAccessDenied: () => void;

	setIsAppLoading: (loading: boolean) => void;
	setRefetching: (refetching: boolean) => void;
	setIsAnonymousMode: (anonymous: boolean) => void;
	updateWorkspaceSavedFilter: (workspaceId: string, savedFilter: string) => void;
	updateWorkspaceHandle: (workspaceId: string, handle: string) => void;
	updateWorkspaceLinkedInEnabled: (workspaceId: string, linkedinEnabled: boolean) => void;
	updateWorkspacePreferences: (workspaceId: string, preferences: WorkspacePreferences) => void;
}

export interface AppWrapperStoreInitialState {
	isAnonymousMode?: boolean;
}

export default function createAppWrapperStore(initialState?: AppWrapperStoreInitialState) {
	return createStore<AppWrapperStoreState>((set) => ({
		// Initial state
		userDetails: null,
		workspaces: [],
		selectedWorkspaceId: '',
		selectedWorkspace: null,
		dashboardMenu: getDashboardMenu({}),
		isApplicantMode: false,

		workspaceSelector: {
			showSelector: false,
			showCreateWorkspace: false,
			callback: () => {},
			source: '',
		},
		pageAccessDenied: {
			workspace: false,
			feature: false,
			message: '',
		},

		isAppLoading: true,
		isRefetching: false,
		isAnonymousMode: initialState?.isAnonymousMode ?? false,

		// Actions
		setUserDetails: (userDetails) => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.userDetails = userDetails;
					if (userDetails) {
						state.workspaces = userDetails.pages || [];
						state.selectedWorkspace =
							userDetails.pages.find((w) => w.id === state.selectedWorkspaceId) ||
							state.selectedWorkspace;
					}
				})
			);
		},

		patchUserDetails: (patch) => {
			set(
				produce((state: AppWrapperStoreState) => {
					if (state.userDetails) {
						state.userDetails = { ...state.userDetails, ...patch };
					}
				})
			);
		},

		setWorkspaces: (workspaces) => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.workspaces = workspaces;
					state.selectedWorkspace =
						state.workspaces.find((w) => w.id === state.selectedWorkspaceId) ||
						state.selectedWorkspace;
				})
			);
		},

		setSelectedWorkspaceId: (workspaceId) => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.selectedWorkspaceId = workspaceId;
					state.selectedWorkspace = state.workspaces.find((w) => w.id === workspaceId) || null;
					state.isApplicantMode = workspaceId === APPLICANT_MODE_WORKSPACE_ID;

					const dashboardMenu = getDashboardMenu({
						selectedWorkspace: state.selectedWorkspace as Workspace,
					});
					state.dashboardMenu = dashboardMenu;
				})
			);
		},

		setWorkspaceSelector: (selector) => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.workspaceSelector = { ...state.workspaceSelector, ...selector };
				})
			);
		},

		resetWorkspaceSelector: () => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.workspaceSelector = {
						showSelector: false,
						showCreateWorkspace: false,
						callback: () => {},
						source: '',
					};
				})
			);
		},

		setPageAccessDenied: (access) => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.pageAccessDenied = { ...state.pageAccessDenied, ...access };
				})
			);
		},

		resetPageAccessDenied: () => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.pageAccessDenied = {
						workspace: false,
						feature: false,
						message: '',
					};
				})
			);
		},

		setIsAppLoading: (loading) => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.isAppLoading = loading;
				})
			);
		},

		setRefetching: (refetching) => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.isRefetching = refetching;
				})
			);
		},

		setIsAnonymousMode: (anonymous) => {
			set(
				produce((state: AppWrapperStoreState) => {
					state.isAnonymousMode = anonymous;
				})
			);
		},

		updateWorkspaceSavedFilter: (workspaceId, savedFilter) => {
			set(
				produce((state: AppWrapperStoreState) => {
					const workspaceIndex = state.workspaces.findIndex((w) => w.id === workspaceId);
					if (workspaceIndex !== -1) {
						state.workspaces[workspaceIndex].savedFilter = savedFilter;
					}
					if (state.selectedWorkspace?.id === workspaceId) {
						state.selectedWorkspace.savedFilter = savedFilter;
					}
				})
			);
		},

		updateWorkspaceHandle: (workspaceId, handle) => {
			set(
				produce((state: AppWrapperStoreState) => {
					const workspaceIndex = state.workspaces.findIndex((w) => w.id === workspaceId);
					if (workspaceIndex !== -1) {
						state.workspaces[workspaceIndex].handle = handle;
					}
					if (state.selectedWorkspace?.id === workspaceId) {
						state.selectedWorkspace.handle = handle;
					}
				})
			);
		},

		updateWorkspaceLinkedInEnabled: (workspaceId, linkedinEnabled) => {
			set(
				produce((state: AppWrapperStoreState) => {
					const workspaceIndex = state.workspaces.findIndex((w) => w.id === workspaceId);
					if (workspaceIndex !== -1) {
						state.workspaces[workspaceIndex].linkedinEnabled = linkedinEnabled;
					}
					if (state.selectedWorkspace?.id === workspaceId) {
						state.selectedWorkspace.linkedinEnabled = linkedinEnabled;
					}
				})
			);
		},

		updateWorkspacePreferences: (workspaceId, preferences) => {
			set(
				produce((state: AppWrapperStoreState) => {
					const workspaceIndex = state.workspaces.findIndex((w) => w.id === workspaceId);
					if (workspaceIndex !== -1) {
						state.workspaces[workspaceIndex].preferences = preferences;
					}
					if (state.selectedWorkspace?.id === workspaceId) {
						state.selectedWorkspace.preferences = preferences;
					}
				})
			);
		},
	}));
}
